import {
  Box,
  Flex,
  Heading,
  Skeleton,
  Spacer,
  Stack,
  useToast,
  Alert,
  AlertIcon,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';
import { useParams } from 'react-router-dom';

import { InvoiceDetailInformation } from '../InvoiceDetailInformation';
import { InvoiceStatusBadge } from '../InvoiceStatusBadge';
import { getInvoiceComputedStatus } from '../utils';
import { InvoiceDetailActionsMenu } from './InvoiceDetailActionsMenu';
import { colors } from 'packages/common-frontend/colors';
import { Breadcrumbs } from 'packages/common-frontend/components/Breadcrumbs';
import { successToastConfig } from 'packages/common-frontend/constants';
import { truncateUuid } from 'packages/common-frontend/utils';
import {
  GetInvoiceByIdDocumentGQLType,
  GetInvoicesByLoanIdDocumentGQLType,
  GetInvoicesByOrganizationIdDocumentGQLType,
  GetPaymentInKindServicingFeeDetailsForCreditFacilitiesDocumentGQLType,
  GetPrincipalPaymentMetricsByLoanIdDocumentGQLType,
  useGetInvoiceByIdQuery,
  useReverseInvoicePaymentMutation,
} from 'packages/invoices/requests/invoices.gql.generated';
import { InvoiceConsolidatedDisbursements } from 'packages/invoices/ui/consolidatedDisbursements/InvoiceConsolidatedDisbursements';
import { InvoiceCalculationComparisonsSideDrawer } from 'packages/invoices/ui/invoiceDetail/InvoiceCalculationComparisonsSideDrawer';
import {
  GetCurrentLiabilityAccountsAndAccrualsForLoanDocumentGQLType,
  GetResolvedLoanByIdDocumentGQLType,
} from 'packages/loans/requests/loans.gql.generated';
import { z } from 'zod/v4';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { SingleEntityDrawer as InvoicesEventLogSingleEntityDrawer } from '~/core/routes/invoices/EntityEventLogDrawers';

const RouteParamsStruct = z.object({
  invoiceId: z.string({ message: 'Missing URL parameter' }),
});

const InvoiceDetailSkeleton = ({ invoiceId }: { invoiceId: string }) => {
  return (
    <Box>
      <Breadcrumbs breadcrumbs={[{ label: 'Invoices', path: '/invoices' }]} />
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mt="24px"
        mb="40px"
      >
        <Heading display="inline-block">{`Invoice #${truncateUuid(
          invoiceId,
        )}`}</Heading>
      </Flex>
      <Skeleton h="600px" />
    </Box>
  );
};

export const InvoiceDetail = () => {
  const params = useParams();
  const { invoiceId } = RouteParamsStruct.parse(params);

  const { user } = useUserAuthOrThrow();

  const toast = useToast();
  const {
    onOpen: onOpenComparisonSideDrawer,
    isOpen: isComparisonSideDrawerOpen,
    onClose: onCloseComparisonSideDrawer,
  } = useDisclosure();

  const {
    data: invoiceData,
    loading: isInvoiceLoading,
  } = useGetInvoiceByIdQuery({
    variables: { invoiceId },
    fetchPolicy: 'no-cache',
  });

  const [
    reverseInvoicePayment,
    { loading: isReverseInvoicePaymentLoading },
  ] = useReverseInvoicePaymentMutation({
    refetchQueries: [
      GetInvoiceByIdDocumentGQLType,
      GetInvoicesByOrganizationIdDocumentGQLType,
      GetInvoicesByLoanIdDocumentGQLType,
      GetResolvedLoanByIdDocumentGQLType,
      GetCurrentLiabilityAccountsAndAccrualsForLoanDocumentGQLType,
      GetPaymentInKindServicingFeeDetailsForCreditFacilitiesDocumentGQLType,
      GetPrincipalPaymentMetricsByLoanIdDocumentGQLType,
    ],
    onCompleted: () => {
      toast({
        ...successToastConfig,
        title: 'Successfully reversed invoice payment',
      });
    },
  });

  if (!invoiceData || isInvoiceLoading) {
    return <InvoiceDetailSkeleton invoiceId={invoiceId} />;
  }

  const invoice = invoiceData.getInvoiceById;

  const computedStatus = getInvoiceComputedStatus({
    status: invoice.status,
    dueDate: invoice.dueDate,
    targetInvoiceDate: invoice.targetInvoiceDate ?? undefined,
  });

  const showDisbursementsAndPaymentInKind =
    invoice.paymentDestinationOrganizationId === user.organizationId;

  return (
    <Box>
      <Breadcrumbs breadcrumbs={[{ label: 'Invoices', path: '/invoices' }]} />
      {isReverseInvoicePaymentLoading && (
        <Alert status="loading" mt="24px">
          <AlertIcon />
          Reversing invoice payment
        </Alert>
      )}
      <Flex alignItems="center" mt="24px" mb="40px">
        <Heading display="inline-block" mr="32px">{`Invoice #${truncateUuid(
          invoiceId,
        )}`}</Heading>
        <InvoiceStatusBadge computedStatus={computedStatus} />
        <Spacer />
        <Flex gap="0.5rem">
          <InvoicesEventLogSingleEntityDrawer entityId={invoiceId} />
          <InvoiceDetailActionsMenu
            invoice={invoice}
            reverseInvoicePayment={() =>
              reverseInvoicePayment({ variables: { invoiceId } })
            }
            isReverseInvoicePaymentLoading={isReverseInvoicePaymentLoading}
          />
        </Flex>
      </Flex>
      <Stack gap="2em">
        <InvoiceDetailInformation
          invoice={invoice}
          mismatchAlertOnClick={onOpenComparisonSideDrawer}
          mismatchAlertOnClickLabel="View Details"
        />
        {showDisbursementsAndPaymentInKind && (
          <Box
            p="40px"
            borderRadius="8px"
            borderColor={colors.gray[100]}
            borderWidth="1px"
          >
            <InvoiceConsolidatedDisbursements invoice={invoice} />
          </Box>
        )}
      </Stack>
      <InvoiceCalculationComparisonsSideDrawer
        invoice={invoice}
        isOpen={isComparisonSideDrawerOpen}
        onClose={onCloseComparisonSideDrawer}
      />
    </Box>
  );
};
